import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ICoreHttpEngine } from './core-http-engine.interface';
import { IGetParams, IPostFileParams, IPostParams, IDownloadFileParams, IHttpResponse } from './core-http-client.models';

/*
  Generated class for the CoreBrowserHttpClientProvider provider.

  See https://angular.io/guide/dependency-injection for more info on providers
  and Angular DI.
* */
@Injectable()
export class CoreBrowserHttpEngine implements ICoreHttpEngine
{

    //#region Constructor

    /**
     * Class constructor
     * @param http 
     */
    constructor(public http: HttpClient)
    {
    }

    //#endregion


    //#region Methods

    /**
     * Core HttpClient get method parsed as JSON
     * @param {IGetParams} params
     */
    public async get<T>(params: IGetParams): Promise<IHttpResponse<T>>
    {
        try
        {
            let result = await this.http.get<T>(params.url, { withCredentials: true, responseType: <any>params.responseType, observe: 'response' }).toPromise();
            return { status: result.status, data: result.body };
        }
        catch (error)
        {
            throw { status: (<HttpErrorResponse>error).status, data: (<HttpErrorResponse>error).error };
        }
    }

    /**
     * Core HttpClient post method 
     * @param {IPostParams} params
     */
    public async post<T>(params: IPostParams): Promise<IHttpResponse<T>>
    {
        try
        {
            let result = await this.http.post<T>(params.url, this.objectToFormData(params.body), { headers: params.headers, withCredentials: true, observe: 'response' }).toPromise();
            return { status: result.status, data: result.body };
        }
        catch (error)
        {
            throw { status: (<HttpErrorResponse>error).status, data: (<HttpErrorResponse>error).error };
        }

    }

    /**
     * Post a file to the specified url
     * @param {IPostFileParams} params
     */
    public postFile<T>(params: IPostFileParams): Promise<IHttpResponse<T>>
    {
        // prepare the request body and populate it
        let requestBody = {}
        requestBody[params.name] = params.value;

        // prepare teh request body
        let postFileParms: IPostParams = {
            url: params.url,
            body: requestBody,
            responseType: params.responseType
        };

        return this.post<T>(postFileParms);
    }

    /**
     * Download a file
     * @param fileName name of the file to be saved 
     * @param url file download url
     */
    public async downloadFile(requestParams: IDownloadFileParams): Promise<void>
    {
        try
        {
            // If the browser is SamsungInternet then the file has to be downloaded directly
            // using the window.location.assign method as otherwise it will block the request.
            if (navigator.userAgent.includes('SamsungBrowser'))
            {
                window.location.assign(requestParams.url)
                return;
            }

            // Perform the get request on the specified url
            let response = await this.http.get(requestParams.url, { observe: 'response', responseType: 'blob', withCredentials: true }).toPromise();

            // Retrieve the body of the request as a blob so that then it can be used for the rest of the download operation.
            // An a tag will be used to perform the download operation instead of opening the file download link in an iframe.
            let blob: Blob = response.body as Blob;
            let blobUrl: string = window.URL.createObjectURL(blob);

            // Create an object Url out of the blob and use it to download the file
            // without need of any unncessary plugins.
            let aTag = document.createElement('a');
            aTag.download = this.extractFileName(response);
            aTag.href = blobUrl;
            aTag.click();

            // Perform the necessary cleanup
            aTag.remove();
            window.URL.revokeObjectURL(blobUrl);
        }
        catch (error)
        {
            throw { status: (<HttpErrorResponse>error).status, data: (<HttpErrorResponse>error).error };
        }
    }

    /**
     * Clear cookies implementation
     */
    public clearCookies(): void
    {
        // do nothing
    }

    /**
     * Transform an KvP object into a FormData object
     * @param values
     */
    private objectToFormData(values: { [key: string]: any }): FormData
    {
        // create a new FormData object
        let formData = new FormData();

        // iterate over all of the keys in the values object
        Object.keys(values)
            .forEach(key =>
            {
                // append each property to the FormData object
                formData.append(key, values[key])
            });

        return formData;
    }

    /**
     * Retrieve the file name from a download file response
     * @param response 
     * @returns 
     */
    private extractFileName(response: HttpResponse<Blob>)
    {
        let headerValue = response.headers.get('Content-Disposition');
        let fileName = headerValue.split('filename=')[1].split(';')[0].replace(/"/g, "");
        return fileName;
    }

    //#endregion
}
